import { Country } from '@afterpay/types'
import Script from 'next/script'
import React, { FunctionComponent, useEffect } from 'react'
import { useCountry } from '../../state'
import { isValidString, valueIsNotNullOrUndefined } from '@afterpay/utils'

declare global {
  interface Window {
    OneTrust?: {
      OnConsentChanged: (arg: () => void) => void
    }
    OnetrustActiveGroups?: string
  }
}

export enum CookieActiveGroup {
  Necessary = 'C0001',
  Performance = 'C0002',
  Functional = 'C0003',
  Marketing = 'C0004',
  SocialMedia = 'C0005',
}

const ONETRUST_STYLE_ID = 'onetrust-style'
const BUTTON_RULE_PATTERN = /button\s*{[^}]*}/g
const ONETRUST_BUTTON_CLASS = 'ot-banner-button'
const ONETRUST_REJECT_BUTTON_ID = 'onetrust-reject-all-handler'
const ONETRUST_ACCEPT_BUTTON_ID = 'onetrust-accept-btn-handler'

export const OneTrust: FunctionComponent = () => {
  const country = useCountry()

  // OneTrust SDK injects a <style id="onetrust-style"> element with global button styles.
  // These global button rules interfere with our checkout buttons (e.g., Option component border-radius).
  // We replace all `button { ... }` selectors with `.ot-banner-button { ... }` and apply that class
  // to the OneTrust buttons, scoping the styles to only affect the cookie banner.
  useEffect(() => {
    if (country !== Country.GB) {
      return
    }

    const scopeOneTrustButtonStyles = (styleElement: HTMLStyleElement): void => {
      if (isValidString(styleElement.textContent)) {
        const buttonRuleMatch = styleElement.textContent.match(BUTTON_RULE_PATTERN)
        if (buttonRuleMatch && buttonRuleMatch.length > 0) {
          styleElement.textContent = styleElement.textContent.replace(BUTTON_RULE_PATTERN, (match) =>
            match.replace(/button\s*{/g, `.${ONETRUST_BUTTON_CLASS} {`)
          )

          const rejectButton = document.getElementById(ONETRUST_REJECT_BUTTON_ID)
          const acceptButton = document.getElementById(ONETRUST_ACCEPT_BUTTON_ID)

          if (valueIsNotNullOrUndefined(rejectButton)) {
            rejectButton.classList.add(ONETRUST_BUTTON_CLASS)
          }
          if (valueIsNotNullOrUndefined(acceptButton)) {
            acceptButton.classList.add(ONETRUST_BUTTON_CLASS)
          }
        }
      }
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLStyleElement && node.id === ONETRUST_STYLE_ID) {
            scopeOneTrustButtonStyles(node)
          }
        })
      })
    })

    observer.observe(document.head, { childList: true })

    const existingStyle = document.getElementById(ONETRUST_STYLE_ID) as HTMLStyleElement | null
    if (existingStyle) {
      scopeOneTrustButtonStyles(existingStyle)
    }

    return () => observer.disconnect()
  }, [country])

  if (country === Country.GB) {
    return (
      <>
        {/*
         * This script must be placed before everything else in order to function correctly.
         * If it is not placed first it will not be able to effectively block cookies from being set.
         */}
        <Script src='https://cdn.cookielaw.org/consent/019721c5-3fd0-74bb-a995-e9d300053761/OtAutoBlock.js' />
        <Script
          src='https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'
          data-domain-script='019721c5-3fd0-74bb-a995-e9d300053761'
          data-document-language='true'
        />
      </>
    )
  } else {
    return <></>
  }
}

export const getOnetrustCookieConsent = (country: Country, activeGroup: CookieActiveGroup): boolean => {
  if (country === Country.GB) {
    if (window?.OnetrustActiveGroups && window?.OnetrustActiveGroups?.length > 0) {
      const activeGroups = window?.OnetrustActiveGroups.split(',')
      const isOptIntoActiveGroup = activeGroups.includes(activeGroup)
      return isOptIntoActiveGroup
    }
    return false
  }

  return true
}

export const onOnetrustCookieConsentChange = (country: Country, activeGroup: CookieActiveGroup, action: () => void): void => {
  if (window?.OneTrust != null) {
    window.OneTrust.OnConsentChanged(() => {
      if (getOnetrustCookieConsent(country, activeGroup)) {
        action()
      }
    })
  }
}
